import { RecordTable } from '../record/record-table';
import { Link } from 'react-router-dom';
import type { OrganisationListEntry } from '@ni/shared';
import { ORGANISATIONS_COLUMNS } from '../../lib/copy';
import { formatDay } from '../../lib/format';

/**
 * **The organisations table** (organisations-as-subjects note §1 R8), in the
 * demo's plain `.table` grammar: Name, Connections, Added.
 *
 * Three columns where People has four, and the difference is the whole of what
 * an organisation is not. There is **no Status**, because neither vertical's
 * vocabulary has a word for an organisation — tracking one writes the record row
 * and no status at all — and a column that could only ever be blank is
 * furniture. There is **no Latest**, because the run kind that would fill it is
 * chunk 3's and a column that could only ever say "never" is the same furniture
 * twice.
 *
 * What is left is the one measure of substance this list has. `connections` is
 * the number the knowledge base shows — how many distinct fact lineages touch
 * this organisation — and it is here as the bare figure, the heading having
 * already said what it counts.
 *
 * **Every row is a link**, unlike People's, whose known rows have no page behind
 * the name. This list is tracked-only, so every row on it has a record.
 */
export function OrganisationsTable({
  entries,
  account = false,
}: {
  entries: readonly OrganisationListEntry[];
  /**
   * **The protection account column**, in a security org and nowhere else
   * (security fold, 2026-09-22) — the People table's rule, for the same reason:
   * in every other vertical the field is `null` on every row.
   */
  account?: boolean;
}) {
  return (
    <RecordTable
      headings={
        account
          ? [
              ORGANISATIONS_COLUMNS.name,
              ORGANISATIONS_COLUMNS.account,
              ORGANISATIONS_COLUMNS.connections,
              ORGANISATIONS_COLUMNS.added,
            ]
          : [
              ORGANISATIONS_COLUMNS.name,
              ORGANISATIONS_COLUMNS.connections,
              ORGANISATIONS_COLUMNS.added,
            ]
      }
    >
      {entries.map((entry) => (
        <tr key={entry.id}>
          <td>
            <Link className="link" to={`/organizations/${entry.id}`}>
              {entry.display_name}
            </Link>
          </td>
          {account ? (
            <td className="quiet-cell">
              {entry.account ? (
                <Link className="link" to={`/accounts/${entry.account.id}`}>
                  {entry.account.label}
                </Link>
              ) : null}
            </td>
          ) : null}
          <td className="quiet-cell">{entry.connections}</td>
          {/* When the office started keeping the record, not when the node was
                minted: an adopted organisation may have sat in the graph for
                months, and what this list is about is the record. */}
          <td className="quiet-cell">{formatDay(entry.created_at)}</td>
        </tr>
      ))}
    </RecordTable>
  );
}
